/**
 * Persistence Service
 * Manages user favorites with a hybrid approach:
 * - Browser Fingerprinting for unique user identification
 * - IndexedDB for local cache
 * - MongoDB (via API) for persistent backup
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs';

// ==================== FINGERPRINTING ====================

let fpPromise = null;
let cachedFingerprint = null;

/**
 * Initialize the fingerprinting library
 */
const initFingerprint = () => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
};

/**
 * Get browser fingerprint (cached after first call)
 * This is a unique ID based on browser/device characteristics
 */
export const getFingerprint = async () => {
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  try {
    const fp = await initFingerprint();
    const result = await fp.get();
    cachedFingerprint = result.visitorId;
    console.log('Browser fingerprint generated:', cachedFingerprint);
    return cachedFingerprint;
  } catch (error) {
    console.error('Error generating fingerprint:', error);
    // Fallback: generate a random ID if fingerprinting fails
    const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    cachedFingerprint = fallbackId;
    return fallbackId;
  }
};

// ==================== INDEXEDDB ====================

const DB_NAME = 'VolleyballFavorites';
const DB_VERSION = 1;
const STORE_NAME = 'favorites';

/**
 * Open IndexedDB connection
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Error opening IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'fingerprintId' });
        console.log('IndexedDB object store created');
      }
    };
  });
};

/**
 * Save favorites to IndexedDB
 */
export const saveToIndexedDB = async (fingerprintId, favorites) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const data = {
      fingerprintId,
      favorites,
      lastUpdated: new Date().toISOString()
    };

    store.put(data);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log('Favorites saved to IndexedDB:', favorites.length);
        resolve();
      };
      transaction.onerror = () => {
        console.error('Error saving to IndexedDB:', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('Error in saveToIndexedDB:', error);
    // Don't throw - IndexedDB is just a cache
  }
};

/**
 * Get favorites from IndexedDB
 */
export const getFromIndexedDB = async (fingerprintId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(fingerprintId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          console.log('Favorites loaded from IndexedDB:', result.favorites.length);
          resolve(result.favorites);
        } else {
          console.log('No favorites found in IndexedDB');
          resolve([]);
        }
      };
      request.onerror = () => {
        console.error('Error reading from IndexedDB:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error in getFromIndexedDB:', error);
    return [];
  }
};

/**
 * Clear IndexedDB (for testing/debugging)
 */
export const clearIndexedDB = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.clear();
    console.log('IndexedDB cleared');
  } catch (error) {
    console.error('Error clearing IndexedDB:', error);
  }
};

// ==================== BACKEND SYNC ====================

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Sync favorites with backend MongoDB
 */
export const syncWithBackend = async (fingerprintId, favorites) => {
  try {
    const response = await fetch(`${API_URL}/api/favorites/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fingerprintId,
        favoriteChampionships: favorites
      })
    });

    if (!response.ok) {
      throw new Error(`Backend sync failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Favorites synced with backend:', data);
    return data;
  } catch (error) {
    console.error('Error syncing with backend:', error);
    // Don't throw - backend sync failure shouldn't break the app
    return null;
  }
};

/**
 * Get favorites from backend MongoDB
 */
export const getFromBackend = async (fingerprintId) => {
  try {
    const response = await fetch(`${API_URL}/api/favorites/${fingerprintId}`);

    if (!response.ok) {
      throw new Error(`Backend fetch failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Favorites loaded from backend:', data.favoriteChampionships.length);
    return data.favoriteChampionships;
  } catch (error) {
    console.error('Error fetching from backend:', error);
    return [];
  }
};

// ==================== UNIFIED API ====================

/**
 * Initialize and load favorites (checks all sources)
 * Priority: Backend (MongoDB) > IndexedDB > Empty array
 */
export const initializeFavorites = async () => {
  try {
    // Get fingerprint first
    const fingerprintId = await getFingerprint();
    console.log('Initializing favorites for fingerprint:', fingerprintId);

    // Try to load from backend first (most reliable)
    let favorites = await getFromBackend(fingerprintId);

    if (favorites.length > 0) {
      console.log('✅ Favorites loaded from backend (MongoDB)');
      // Update IndexedDB cache
      await saveToIndexedDB(fingerprintId, favorites);
      return { fingerprintId, favorites };
    }

    // Fallback to IndexedDB if backend has no data
    favorites = await getFromIndexedDB(fingerprintId);

    if (favorites.length > 0) {
      console.log('✅ Favorites loaded from IndexedDB cache');
      // Sync to backend for future use
      await syncWithBackend(fingerprintId, favorites);
      return { fingerprintId, favorites };
    }

    // No favorites found anywhere
    console.log('ℹ️ No favorites found - starting fresh');
    return { fingerprintId, favorites: [] };

  } catch (error) {
    console.error('Error initializing favorites:', error);
    return { fingerprintId: null, favorites: [] };
  }
};

/**
 * Save favorites to all locations (IndexedDB + Backend)
 */
export const saveFavorites = async (fingerprintId, favorites) => {
  try {
    console.log('Saving favorites:', favorites.length);

    // Save to IndexedDB (fast, local)
    await saveToIndexedDB(fingerprintId, favorites);

    // Sync to backend (persistent, cloud)
    await syncWithBackend(fingerprintId, favorites);

    console.log('✅ Favorites saved successfully');
  } catch (error) {
    console.error('Error saving favorites:', error);
  }
};

/**
 * Utility: Get current fingerprint (if already initialized)
 */
export const getCurrentFingerprint = () => {
  return cachedFingerprint;
};
